/**
 * ModalOpenContext – Sprint B (B1): No refresh while any modal is open.
 * Exposes isModalOpen and register/unregister so dialogs can block periodic and focus-triggered refetches.
 */
import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface ModalOpenContextType {
  isModalOpen: boolean;
  registerModal: () => void;
  unregisterModal: () => void;
}

const ModalOpenContext = createContext<ModalOpenContextType | undefined>(undefined);

export function ModalOpenProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);

  const registerModal = useCallback(() => {
    setCount((c) => c + 1);
  }, []);

  const unregisterModal = useCallback(() => {
    setCount((c) => Math.max(0, c - 1));
  }, []);

  const isModalOpen = count > 0;

  return (
    <ModalOpenContext.Provider
      value={{ isModalOpen, registerModal, unregisterModal }}
    >
      {children}
    </ModalOpenContext.Provider>
  );
}

export function useModalOpen(): ModalOpenContextType {
  const ctx = useContext(ModalOpenContext);
  if (ctx === undefined) {
    throw new Error('useModalOpen must be used within ModalOpenProvider');
  }
  return ctx;
}

/**
 * Call from inside a Dialog: when `open` is true the modal is counted as open, so refresh is blocked.
 * Call once at top level of the component that renders the Dialog, passing the dialog's open state.
 */
export function useModalOpenRegistration(open: boolean) {
  const { registerModal, unregisterModal } = useModalOpen();

  useEffect(() => {
    if (open) {
      registerModal();
      return () => {
        unregisterModal();
      };
    }
  }, [open, registerModal, unregisterModal]);
}
